/**
 * Tiny CSV serializer + browser-side save helpers used by the table
 * Export feature.
 *
 * Output uses CRLF line endings and quotes any field containing a
 * comma, double quote, or newline (with internal quotes doubled), per
 * RFC 4180. NULL values are written as the empty string.
 */
import type { ColumnMeta } from "../api/types";

export function csvEscape(v: unknown): string {
  if (v == null) return "";
  const str = String(v);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function rowsToCsv(
  columns: ColumnMeta[],
  rows: Record<string, unknown>[],
): string {
  const header = columns.map((c) => csvEscape(c.name)).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => csvEscape(r[c.name])).join(","),
  );
  return [header, ...lines].join("\r\n");
}

/**
 * Save a CSV string to the user's filesystem. Uses the File System
 * Access API where available (Chromium 86+, Edge, Opera) so the user
 * gets a real "Save As" dialog and can pick the destination folder.
 * Falls back to the classic <a download> trick on Safari/Firefox where
 * the file simply lands in the default download folder.
 *
 * Returns `true` if the file was saved, `false` if the user cancelled.
 */
export async function saveCsv(
  suggestedName: string,
  content: string,
): Promise<boolean> {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });

  type SaveFilePicker = (opts: {
    suggestedName: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<{
    createWritable: () => Promise<{
      write: (data: Blob | string) => Promise<void>;
      close: () => Promise<void>;
    }>;
  }>;
  const showSaveFilePicker = (
    window as Window & { showSaveFilePicker?: SaveFilePicker }
  ).showSaveFilePicker;

  if (typeof showSaveFilePicker === "function") {
    try {
      const handle = await showSaveFilePicker({
        suggestedName,
        types: [
          {
            description: "CSV file",
            accept: { "text/csv": [".csv"] },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (err) {
      // User cancelled the dialog — not an error worth surfacing.
      if ((err as Error).name === "AbortError") return false;
      // Otherwise fall through to anchor download below.
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}

/** Default filename for a table export, includes today's date. */
export function defaultExportFilename(schema: string, name: string): string {
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `${schema}.${name}_${stamp}.csv`;
}
